class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.autoCloseTimer = null; // Timer for auto-close functionality
    this.countdownTimer = null; // Timer for countdown display
    this.remainingTime = 5; // Remaining time in seconds

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
        // Start auto-close timer after animation completes
        this.startAutoCloseTimer();
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    // Pause auto-close on hover
    this.notification.addEventListener('mouseenter', this.pauseAutoClose.bind(this));
    this.notification.addEventListener('mouseleave', this.resumeAutoClose.bind(this));
  }

  close() {
    this.notification.classList.remove('active', 'paused');
    document.body.removeEventListener('click', this.onBodyClick);

    // Clear auto-close timer and countdown
    this.clearAutoCloseTimer();

    // Remove hover event listeners
    this.notification.removeEventListener('mouseenter', this.pauseAutoClose.bind(this));
    this.notification.removeEventListener('mouseleave', this.resumeAutoClose.bind(this));

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Auto-close timer methods
  startAutoCloseTimer() {
    this.clearAutoCloseTimer(); // Clear any existing timer
    this.remainingTime = 5; // Reset to 5 seconds

    // Reset progress bar
    const progressBar = this.notification.querySelector('.cart-notification__progress-bar');
    if (progressBar) {
      progressBar.style.transform = 'translateX(-100%)';
      progressBar.style.transition = 'none';

      // Force reflow
      progressBar.offsetHeight;

      // Start progress animation
      progressBar.style.transition = 'transform 5s linear';
      progressBar.style.transform = 'translateX(0)';
    }

    // Reset and start circular timer
    this.resetCircularTimer();
    this.startCircularTimer();

    // Start countdown display
    this.startCountdown();

    this.autoCloseTimer = setTimeout(() => {
      this.close();
    }, 5000); // 5 seconds
  }

  clearAutoCloseTimer() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    // Clear countdown timer
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    // Reset progress bar
    const progressBar = this.notification.querySelector('.cart-notification__progress-bar');
    if (progressBar) {
      progressBar.style.transform = 'translateX(-100%)';
      progressBar.style.transition = 'none';
    }

    // Reset circular timer
    this.resetCircularTimer();
  }

  pauseAutoClose() {
    this.clearAutoCloseTimer();
    this.notification.classList.add('paused');

    // Pause progress bar animation
    const progressBar = this.notification.querySelector('.cart-notification__progress-bar');
    if (progressBar) {
      const computedStyle = window.getComputedStyle(progressBar);
      const currentTransform = computedStyle.transform;
      progressBar.style.transform = currentTransform;
      progressBar.style.transition = 'none';
    }

    // Pause circular timer
    this.pauseCircularTimer();
  }

  resumeAutoClose() {
    this.notification.classList.remove('paused');

    // For simplicity, just restart the timer when resuming
    // This provides consistent behavior across all browsers
    this.startAutoCloseTimer();
  }

  // Countdown display methods
  startCountdown() {
    this.updateCountdownDisplay();
    this.countdownTimer = setInterval(() => {
      this.remainingTime--;
      this.updateCountdownDisplay();

      if (this.remainingTime <= 0) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }, 1000);
  }

  updateCountdownDisplay() {
    // Update circular timer text
    const timerText = this.notification.querySelector('.cart-notification__timer-text');
    if (timerText) {
      timerText.textContent = this.remainingTime > 0 ? this.remainingTime : '0';
    }
  }

  // Circular timer methods
  resetCircularTimer() {
    const progressCircle = this.notification.querySelector('.cart-notification__timer-progress');
    if (progressCircle) {
      // Reset to full circle (no progress)
      progressCircle.style.strokeDashoffset = '113.1';
      progressCircle.style.transition = 'none';
    }
  }

  startCircularTimer() {
    const progressCircle = this.notification.querySelector('.cart-notification__timer-progress');
    if (progressCircle) {
      // Force reflow
      progressCircle.offsetHeight;

      // Start the circular progress animation
      progressCircle.style.transition = 'stroke-dashoffset 5s linear';
      progressCircle.style.strokeDashoffset = '0';
    }
  }

  pauseCircularTimer() {
    const progressCircle = this.notification.querySelector('.cart-notification__timer-progress');
    if (progressCircle) {
      // Get current progress and freeze it
      const computedStyle = window.getComputedStyle(progressCircle);
      const currentOffset = computedStyle.strokeDashoffset;
      progressCircle.style.strokeDashoffset = currentOffset;
      progressCircle.style.transition = 'none';
    }
  }
}

customElements.define('cart-notification', CartNotification);
